import React from 'react';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import { PickerColumn } from './tableColumns';

export const TARIFF_TABLE_KEY = 'tariff-run-lines';

type Row = Record<string, any>;

export type TariffColumnKey =
  | 'description'
  | 'country'
  | 'spi'
  | 'status'
  | 'product_value'
  | 'quantity'
  | 'transport'
  | 'entry_date'
  | 'base_duty_rate'
  | 'base_duty'
  | 'additional_duty_rate'
  | 'additional_duty'
  | 'total_duty'
  | 'fees'
  | 'total_cost'
  | 'metal_content'
  | 'product_code'
  | 'review_status'
  | 'last_calculated';

export interface TariffColumn extends PickerColumn<TariffColumnKey> {
  header: string;
  sortKey?: string;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  render: (item: Row) => React.ReactNode;
}

const getAdditionalDutyPercent = (math: any, inputs: any): string => {
  if (!math || !inputs) return '-';
  const { additionalDutyDollars } = math;
  const { productValue } = inputs;
  if (!productValue || productValue === 0) return '0%';
  const percentage = (additionalDutyDollars / productValue) * 100;
  return `${percentage
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')}%`;
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const money = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '--';
  const n = Number(v);
  return Number.isFinite(n) ? usd.format(n) : '--';
};

// Entry dates are picked as a local calendar day and saved as that day's local
// midnight, so they are shown in local time — the same as the tariff detail page.
const localDate = (v: unknown): string =>
  v ? moment(v as string).format('MM/DD/YYYY') : '--';

const reviewStatusLabel = (flag: unknown): string =>
  flag
    ? String(flag)
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : '--';

const metalContent = (item: Row): React.ReactNode => {
  const parts = [
    {
      label: 'Steel',
      value: item.steel_value,
      country: item.pour_country,
      place: 'pour',
    },
    {
      label: 'Aluminum',
      value: item.aluminum_value,
      country: item.smelt_country,
      place: 'smelt',
    },
    { label: 'Copper', value: item.copper_value },
  ].filter((p) => p.value !== null && p.value !== undefined && p.value !== '');
  if (!parts.length) return '--';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {parts.map((p) => (
        <span key={p.label} style={{ whiteSpace: 'nowrap' }}>
          {p.label} {money(p.value)}
          {p.country && (
            <span style={{ color: 'var(--text-secondary)' }}>
              {' '}
              ({p.place}: {p.country})
            </span>
          )}
        </span>
      ))}
    </div>
  );
};

const statusChip = (item: Row): React.ReactNode => {
  const statusColor = item.latest_tariff_result?.status?.color;
  const message = item.latest_tariff_result?.status?.message;
  if (statusColor === 'Green') {
    return <span className='screening-badge chip-green lens-chip'>Green</span>;
  }
  if (statusColor === 'Yellow') {
    return (
      <Tooltip title={message} placement='top'>
        <span className='screening-badge chip-yellow lens-chip'>Yellow</span>
      </Tooltip>
    );
  }
  if (statusColor === 'Red') {
    return (
      <Tooltip title={message} placement='top'>
        <span className='screening-badge chip-red lens-chip'>Red</span>
      </Tooltip>
    );
  }
  return null;
};

const math = (item: Row): Row => item.latest_tariff_result?.math || {};

export const TARIFF_COLUMNS: readonly TariffColumn[] = [
  {
    key: 'description',
    label: 'Description',
    header: 'DESCRIPTION',
    sortKey: 'description',
    cellStyle: {
      maxWidth: 220,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    render: (item) => (
      <span title={item.description || undefined}>
        {item.description || '--'}
      </span>
    ),
  },
  {
    key: 'country',
    label: 'Country',
    defaultVisible: true,
    header: 'COUNTRY',
    sortKey: 'country_of_origin',
    headerStyle: { width: '8%' },
    render: (item) => item.country_of_origin || '--',
  },
  {
    // The claimed Special Program Indicator. It is what explains a Free base
    // rate: an SPI the origin qualifies for zeroes the base duty and can clear
    // Ch.99 duties too, so two rows differing only by SPI would otherwise
    // render identically while producing very different totals.
    key: 'spi',
    label: 'SPI',
    defaultVisible: true,
    header: 'SPI',
    headerStyle: { width: '6%' },
    render: (item) =>
      item.spi_code ? (
        <span className='screening-badge chip-blue lens-chip'>
          {item.spi_code}
        </span>
      ) : (
        '--'
      ),
  },
  {
    key: 'status',
    label: 'Status',
    defaultVisible: true,
    header: 'STATUS',
    headerStyle: { width: '10%' },
    render: statusChip,
  },
  {
    key: 'product_value',
    label: 'Product Value',
    header: 'PRODUCT VALUE',
    sortKey: 'product_value',
    render: (item) =>
      money(
        item.latest_tariff_result?.inputs?.productValue ?? item.product_value,
      ),
  },
  {
    key: 'quantity',
    label: 'Quantity',
    header: 'QTY',
    sortKey: 'quantity',
    cellStyle: { whiteSpace: 'nowrap' },
    render: (item: Row): React.ReactNode => {
      if (
        item.quantity === null ||
        item.quantity === undefined ||
        item.quantity === ''
      )
        return '--';
      const n = Number(item.quantity);
      const amount = Number.isFinite(n)
        ? n.toLocaleString('en-US', { maximumFractionDigits: 5 })
        : String(item.quantity);
      return item.quantity_uom ? `${amount} ${item.quantity_uom}` : amount;
    },
  },
  {
    key: 'transport',
    label: 'Mode of Transport',
    header: 'TRANSPORT',
    sortKey: 'trans_method',
    render: (item) => item.trans_method || '--',
  },
  {
    key: 'entry_date',
    label: 'Entry Date',
    header: 'ENTRY DATE',
    sortKey: 'entry_date',
    cellStyle: { whiteSpace: 'nowrap' },
    render: (item) => localDate(item.entry_date),
  },
  {
    key: 'base_duty_rate',
    label: 'Base Duty Rate',
    defaultVisible: true,
    header: 'BASE DUTY RATE',
    sortKey: 'base_duty_rate',
    headerStyle: { width: '12%' },
    render: (item) => item?.latest_tariff_result?.base?.baseDutyRate || '-',
  },
  {
    key: 'base_duty',
    label: 'Base Duty $',
    header: 'BASE DUTY',
    sortKey: 'base_duty',
    render: (item) => money(math(item).baseAmountDollars),
  },
  {
    key: 'additional_duty_rate',
    label: 'Additional Duty Rate',
    defaultVisible: true,
    header: 'ADDITIONAL DUTY RATE',
    sortKey: 'additional_duty_rate',
    headerStyle: { width: '16%' },
    render: (item) =>
      item?.latest_tariff_result?.math
        ? getAdditionalDutyPercent(
            math(item),
            item?.latest_tariff_result?.inputs,
          )
        : '-',
  },
  {
    key: 'additional_duty',
    label: 'Additional Duty $',
    header: 'ADDITIONAL DUTY',
    sortKey: 'additional_duty',
    render: (item) => money(math(item).additionalDutyDollars),
  },
  {
    key: 'total_duty',
    label: 'Total Duty $',
    header: 'TOTAL DUTY',
    sortKey: 'total_duty',
    render: (item) => money(math(item).dutyCost),
  },
  {
    key: 'fees',
    label: 'MPF + HMF',
    defaultVisible: true,
    header: 'MPF + HMF',
    sortKey: 'hmf_plus_mpf',
    headerStyle: { width: '12%' },
    render: (item) =>
      `$${((math(item).mpf || 0) + (math(item).hmf || 0)).toFixed(2)}`,
  },
  {
    key: 'total_cost',
    label: 'Total Cost',
    defaultVisible: true,
    header: 'TOTAL COST',
    sortKey: 'total_cost',
    headerStyle: { width: '12%' },
    render: (item) => `$${(math(item).totalCost || 0).toFixed(2)}`,
  },
  {
    key: 'metal_content',
    label: 'Metal Content',
    header: 'METAL CONTENT',
    render: metalContent,
  },
  {
    key: 'product_code',
    label: 'Product Code',
    defaultVisible: true,
    header: 'PRODUCT CODE',
    sortKey: 'product_code',
    headerStyle: { width: '12%' },
    render: (item) => item.product_code || '--',
  },
  {
    key: 'review_status',
    label: 'Review Status',
    header: 'REVIEW STATUS',
    sortKey: 'status_flag',
    cellStyle: { whiteSpace: 'nowrap' },
    render: (item) => reviewStatusLabel(item.status_flag),
  },
  {
    key: 'last_calculated',
    label: 'Last Calculated',
    header: 'LAST CALCULATED',
    sortKey: 'last_calculated',
    cellStyle: { whiteSpace: 'nowrap' },
    render: (item) => localDate(item.latest_saved_at),
  },
];
